import React, { useCallback, useEffect, useRef, useState } from "react";
import SearchBox from "../../../../common/components/search/searchBox";

// Shared detail-pane scaffold for navigation layouts that need search, grouped items, and footer content.
const renderNavGroup = ({ key, label, items, className = "" }, index) => {
  const visibleItems = React.Children.toArray(items).filter(Boolean);

  if (visibleItems.length === 0) {
    return null;
  }

  return (
    <section
      key={key || `${label || "default"}-${index}`}
      className={["sidebar-nav-group", className].filter(Boolean).join(" ")}
    >
      {label ? <div className="sidebar-nav-group-label">{label}</div> : null}
      <div className="sidebar-nav-group-items">{visibleItems}</div>
    </section>
  );
};

const NavigationDetailContent = ({
  className = "",
  groups = [],
  footerGroups = [],
  searchLabel,
  searchValue,
  onSearchChange,
  showSearch = true,
  isSearching = false,
  emptyStateLabel = "",
}) => {
  const contentRef = useRef(null);
  const [hasScrolledContent, setHasScrolledContent] = useState(false);

  const syncScrolledState = useCallback(() => {
    const scrollContainer = contentRef.current;
    setHasScrolledContent(scrollContainer ? scrollContainer.scrollTop > 0 : false);
  }, []);

  useEffect(() => {
    const scrollContainer = contentRef.current;

    if (!scrollContainer) {
      setHasScrolledContent(false);
      return undefined;
    }

    syncScrolledState();
    scrollContainer.addEventListener("scroll", syncScrolledState, {
      passive: true,
    });

    return () => {
      scrollContainer.removeEventListener("scroll", syncScrolledState);
    };
  }, [syncScrolledState]);

  useEffect(() => {
    syncScrolledState();
  }, [footerGroups, groups, showSearch, syncScrolledState]);

  const hasVisibleMenuResults = groups.some(
    (group) => React.Children.toArray(group?.items).filter(Boolean).length > 0
  );
  const hasVisibleFooterResults = footerGroups.some(
    (group) => React.Children.toArray(group?.items).filter(Boolean).length > 0
  );

  return (
    <div
      className={["sidebar-content", className].filter(Boolean).join(" ")}
      ref={contentRef}
    >
      {showSearch ? (
        <div
          className={`menu-search-wrapper sidebar-global-search${
            hasScrolledContent ? " is-scrolled" : ""
          }`}
          role="search"
        >
          <SearchBox
            placeholder={searchLabel}
            value={searchValue}
            onChange={onSearchChange}
            aria-label={searchLabel}
          />
        </div>
      ) : null}

      <div className="sidebar-menu">
        {groups.map(renderNavGroup)}

        {!hasVisibleMenuResults && !hasVisibleFooterResults && isSearching && (
          <div className="sidebar-nav-search-empty-state">{emptyStateLabel}</div>
        )}
      </div>

      {hasVisibleFooterResults ? (
        <div className="sidebar-footer">{footerGroups.map(renderNavGroup)}</div>
      ) : null}
    </div>
  );
};

export default NavigationDetailContent;
